document.addEventListener("DOMContentLoaded", () => {
  const navToggle = document.querySelector(".nav-toggle");
  const nav = document.getElementById("main-navigation");
  const body = document.querySelector('body');

  if (!navToggle || !nav) return;

  // Read the breakpoint from CSS so CSS/JS stay in sync
  const getBpPx = () => {
    const raw = getComputedStyle(document.documentElement)
      .getPropertyValue('--nav-md-breakpoint').trim();
    return parseInt(raw, 10) || 990;
  };
  
  const isMobile = () => window.innerWidth <= getBpPx();


  // Force close all dropdowns
  const closeAllDropdowns = () => {
    nav.querySelectorAll('li.is-open').forEach(li => {
      li.classList.remove('is-open');
      const link = li.querySelector('a');
      if (link) link.removeAttribute('aria-expanded');
    });
  };

  // Close sibling dropdowns
  const closeSiblings = (currentLi) => {
    const parent = currentLi.parentElement;
    if (!parent) return;
    
    parent.querySelectorAll(':scope > li.is-open').forEach(li => {
      if (li !== currentLi) {
        li.classList.remove('is-open');
        const link = li.querySelector('a');
        if (link) link.removeAttribute('aria-expanded');
      }
    });
  };

  // Mobile/Desktop state management
  const updateNavMode = () => {
    const mobile = isMobile();
    
    if (mobile) {
      // Mobile mode: disable all hover, ensure click-only
      document.body.classList.add('mobile-nav');
      
      // Force close any open dropdowns when switching to mobile
      closeAllDropdowns();
      
    } else {
      // Desktop mode: re-enable hover
      document.body.classList.remove('mobile-nav');
      
      // Close mobile nav if open
      nav.classList.remove('is-open');
      navToggle.classList.remove('is-active');
      navToggle.setAttribute('aria-expanded', 'false');

      body.classList.remove('scroll-lock');
      
      // Close all dropdowns with delay to avoid harsh transition
      setTimeout(() => {
        closeAllDropdowns();
      }, 100);
    }
  };

  // Initial setup
  updateNavMode();

  // Hamburger toggle
  navToggle.addEventListener("click", () => {
    const expanded = navToggle.getAttribute("aria-expanded") === "true";
    navToggle.setAttribute("aria-expanded", String(!expanded));
    navToggle.classList.toggle("is-active");
    nav.classList.toggle("is-open");
    body.classList.toggle('scroll-lock');
    
    // Close all dropdowns when closing main nav
    if (!nav.classList.contains('is-open')) {
      // Ensure mobile class stays during animation
      document.body.classList.add('mobile-nav');
      
      // Close dropdowns immediately - CSS will handle the animation
      closeAllDropdowns();
    }
  });

  // Handle clicks on parent items
  nav.addEventListener("click", (e) => {
    const link = e.target.closest("a");
    if (!link) return;

    const li = link.closest("li");
    if (!li) return;

    // Check if this is a parent item
    const hasSubmenu = !!li.querySelector(":scope > ul.sub-menu");
    const hasMega = !!li.querySelector(":scope > .mega-menu");
    const hasAriaHaspopup = link.hasAttribute("aria-haspopup");
    
    if (!hasSubmenu && !hasMega && !hasAriaHaspopup) return;

    if (isMobile()) {
      // MOBILE: Prevent default navigation and handle with JavaScript
      e.preventDefault();
      e.stopPropagation();

      const isOpen = li.classList.contains("is-open");
      
      if (isOpen) {
        // Close this dropdown
        li.classList.remove("is-open");
        link.setAttribute("aria-expanded", "false");
      } else {
        // Close siblings first
        closeSiblings(li);
        
        // Open this dropdown
        li.classList.add("is-open");
        link.setAttribute("aria-expanded", "true");
      }
    } else {
      // DESKTOP: Allow navigation for parent links, but manage ARIA for screen readers
      const isOpen = li.matches(":hover");
      if (isOpen) {
        link.setAttribute("aria-expanded", "true");
      } else {
        link.setAttribute("aria-expanded", "false");
      }
    }
  });

  // Handle resize
  let lastMobile = isMobile();
  window.addEventListener("resize", () => {
    const nowMobile = isMobile();
    if (nowMobile !== lastMobile) {
      updateNavMode();
      disableHoverOnMobile();
      lastMobile = nowMobile;
    }
  });

  // Completely disable hover effects on mobile
  const disableHoverOnMobile = () => {
    if (isMobile()) {
      // Add CSS class to disable hover
      document.documentElement.classList.add('no-hover');
      
      // Also prevent mouseenter/mouseleave events
      nav.addEventListener('mouseenter', preventHover, true);
      nav.addEventListener('mouseleave', preventHover, true);
      nav.addEventListener('mouseover', preventHover, true);
      nav.addEventListener('mouseout', preventHover, true);
    } else {
      document.documentElement.classList.remove('no-hover');
      nav.removeEventListener('mouseenter', preventHover, true);
      nav.removeEventListener('mouseleave', preventHover, true);
      nav.removeEventListener('mouseover', preventHover, true);
      nav.removeEventListener('mouseout', preventHover, true);
    }
  };
  
  const preventHover = (e) => {
    if (isMobile()) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return false;
    }
  };

  // Apply initial hover prevention
  disableHoverOnMobile();

  // Keyboard navigation support
  nav.addEventListener("keydown", (e) => {
    const link = e.target.closest("a");
    if (!link) return;

    const li = link.closest("li");
    if (!li) return;

    // Check if this is a parent item
    const hasSubmenu = !!li.querySelector(":scope > ul.sub-menu");
    const hasMega = !!li.querySelector(":scope > .mega-menu");
    const hasAriaHaspopup = link.hasAttribute("aria-haspopup");
    
    if (!hasSubmenu && !hasMega && !hasAriaHaspopup) return;

    // Handle Enter and Space keys
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      
      const isOpen = li.classList.contains("is-open");
      
      if (isOpen) {
        li.classList.remove("is-open");
        link.setAttribute("aria-expanded", "false");
      } else {
        closeSiblings(li);
        li.classList.add("is-open");
        link.setAttribute("aria-expanded", "true");
      }
    }
    
    // Handle Escape key - close current dropdown
    if (e.key === "Escape") {
      li.classList.remove("is-open");
      link.setAttribute("aria-expanded", "false");
      link.focus();
    }
  });

  // Focus management for better accessibility
  nav.addEventListener("focusin", (e) => {
    const link = e.target.closest("a");
    if (!link) return;
    
    const li = link.closest("li");
    if (!li) return;
    
    // If focusing on a link inside an open dropdown, keep it open
    const parentLi = li.closest("li.is-open");
    if (parentLi) return;
    
    // Close other dropdowns when focusing on a different parent
    const hasSubmenu = !!li.querySelector(":scope > ul.sub-menu");
    const hasMega = !!li.querySelector(":scope > .mega-menu");
    
    if (hasSubmenu || hasMega) {
      closeSiblings(li);
    }
  });

  // Close dropdowns when focus leaves the navigation
  document.addEventListener("focusin", (e) => {
    if (!nav.contains(e.target)) {
      closeAllDropdowns();
    }
  });
});
